import { Box, ListItem, OrderedList, VStack } from '@chakra-ui/react';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import Body2 from '@components/typography/Body2';
import TypographyHeader from '@components/typography/Header';
import Link from 'next/link';

const PrivacyPolicy = () => {
  return (
    <DefaultLayout>
      <Box bg="blue.50" pt={120} p={10} px={[8, 8, 16, 40]}>
        <VStack spacing={6} align="stretch">
          <TypographyHeader as="h1">PRIVACY POLICY</TypographyHeader>
          <Body2>Effective date: 12/09/2020</Body2>

          <TypographyHeader as="h2">1. Introduction</TypographyHeader>
          <Body2>
            Help Button, Inc. dba Fair Square Medicare and its affiliates, including Virgil Insurance Agency, LLC dba
            Fair Square Medicare (collectively "Fair Square Medicare", "Company", "we", "us", "Service"), provide
            services to educate and advise individuals regarding healthcare matters. We also provide healthcare advisory
            and brokerage services to help individuals choose additional Medicare coverage and other types of health
            coverage for their needs. This Privacy Policy is designed to help you understand how we collect, use, and
            share your personal information and to help you understand and exercise your privacy rights.
          </Body2>

          <Body2>
            We use your data to provide and improve Service. By using Service, you agree to the collection and use of
            information in accordance with this policy. Unless otherwise defined in this Privacy Policy, the terms used
            in this Privacy Policy have the same meanings as in our Terms and Conditions.
          </Body2>

          <Body2>
            Our Terms and Conditions ("<b>Terms</b>") govern all use of our Service and together with the Privacy Policy
            constitutes your agreement with us ("<b>agreement</b>").
          </Body2>

          <TypographyHeader as="h2">2. Definitions</TypographyHeader>
          <Body2>
            <b>SERVICE </b>means the www.fairsquaremedicare.com website operated by Help Button Inc (d.b.a Fair Square
            Medicare)
          </Body2>

          <Body2>
            <b>PERSONAL DATA</b> means data about a living individual who can be identified from those data (or from
            those and other information either in our possession or likely to come into our possession).
          </Body2>

          <Body2>
            <b>USAGE DATA</b> is data collected automatically either generated by the use of Service or from Service
            infrastructure itself (for example, the duration of a page visit).
          </Body2>

          <Body2>
            <b>COOKIES</b> are small files stored on your device (computer or mobile device).
          </Body2>

          <Body2>
            <b>DATA CONTROLLER</b> means a natural or legal person who (either alone or jointly or in common with other
            persons) determines the purposes for which and the manner in which any personal data are, or are to be,
            processed. For the purpose of this Privacy Policy, we are a Data Controller of your data.
          </Body2>

          <Body2>
            <b>DATA PROCESSORS (OR SERVICE PROVIDERS)</b> means any natural or legal person who processes the data on
            behalf of the Data Controller. We may use the services of various Service Providers in order to process your
            data more effectively.
          </Body2>

          <Body2>
            <b>DATA SUBJECT </b>is any living individual who is the subject of Personal Data.
          </Body2>

          <Body2>
            <b>THE USER </b>is the individual using our Service. The User corresponds to the Data Subject, who is the
            subject of Personal Data.
          </Body2>

          <TypographyHeader as="h2">3. Information Collection and Use</TypographyHeader>
          <Body2>
            We collect several different types of information for various purposes to provide and improve our Service to
            you.
          </Body2>

          <TypographyHeader as="h2">4. Types of Data Collected</TypographyHeader>
          <Body2 fontWeight="bold">Personal Data</Body2>
          <Body2>
            While using our Service, we may ask you to provide us with certain personally identifiable information that
            can be used to contact or identify you ("<b>Personal Data</b>"). Personally identifiable information may
            include, but is not limited to:
          </Body2>

          <OrderedList pl={4} spacing={2} mb={6}>
            <ListItem>
              <Body2>Email address</Body2>
            </ListItem>
            <ListItem>
              <Body2>First name and last name</Body2>
            </ListItem>
            <ListItem>
              <Body2>Phone number</Body2>
            </ListItem>
            <ListItem>
              <Body2>Cookies and Usage Data</Body2>
            </ListItem>
          </OrderedList>

          <Body2>
            We may use your Personal Data to contact you with newsletters, marketing or promotional materials and other
            information that may be of interest to you. You may opt out of receiving any, or all, of these
            communications from us by following the unsubscribe link.
          </Body2>

          <Body2 fontWeight="bold">Usage Data</Body2>
          <Body2>
            We may also collect information that your browser sends whenever you visit our Service or when you access
            Service by or through a mobile device ("<b>Usage Data</b>").
          </Body2>

          <Body2>
            This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address),
            browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the
            time spent on those pages, unique device identifiers and other diagnostic data.
          </Body2>

          <Body2>
            When you access Service with a mobile device, this Usage Data may include information such as the type of
            mobile device you use, your mobile device unique ID, the IP address of your mobile device, your mobile
            operating system, the type of mobile Internet browser you use, unique device identifiers and other
            diagnostic data.
          </Body2>

          <Body2>
            <b>Tracking Cookies Data</b>
          </Body2>
          <Body2>
            We use cookies and similar tracking technologies to track the activity on our Service and we hold certain
            information.
          </Body2>

          <Body2>
            Cookies are files with a small amount of data which may include an anonymous unique identifier. Cookies are
            sent to your browser from a website and stored on your device. Other tracking technologies are also used
            such as beacons, tags and scripts to collect and track information and to improve and analyze our Service.
          </Body2>

          <Body2>
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if
            you do not accept cookies, you may not be able to use some portions of our Service.
          </Body2>

          <Body2>Examples of Cookies we use:</Body2>

          <OrderedList pl={4} spacing={2} mb={6}>
            <ListItem>
              <Body2>
                <b>Session Cookies:</b> We use Session Cookies to operate our Service.
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>
                <b>Preference Cookies:</b> We use Preference Cookies to remember your preferences and various settings.
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>
                <b>Security Cookies:</b> We use Security Cookies for security purposes.
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>
                <b>Advertising Cookies:</b> Advertising Cookies are used to serve you with advertisements that may be
                relevant to you and your interests.
              </Body2>
            </ListItem>
          </OrderedList>

          <Body2>
            <b>Other Data</b>
          </Body2>
          <Body2>
            While using our Service, we may also collect the following information: sex, age, date of birth, place of
            birth, passport details, citizenship, registration at place of residence and actual address, telephone
            number (work, mobile), details of documents on education, qualification, professional training, employment
            agreements, non-disclosure agreements, information on bonuses and compensation, information on marital
            status, family members, social security (or other taxpayer identification) number, office location and other
            data.
          </Body2>

          <TypographyHeader as="h2">5. Use of Data</TypographyHeader>
          <Body2>Help Button Inc (d.b.a Fair Square Medicare) uses the collected data for various purposes:</Body2>
          <OrderedList pl={4} spacing={2} mb={6}>
            <ListItem>
              <Body2>to provide and maintain our Service;</Body2>
            </ListItem>
            <ListItem>
              <Body2>to notify you about changes to our Service;</Body2>
            </ListItem>
            <ListItem>
              <Body2>
                to allow you to participate in interactive features of our Service when you choose to do so;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>to provide customer support;</Body2>
            </ListItem>
            <ListItem>
              <Body2>to gather analysis or valuable information so that we can improve our Service;</Body2>
            </ListItem>
            <ListItem>
              <Body2>to monitor the usage of our Service;</Body2>
            </ListItem>
            <ListItem>
              <Body2>to detect, prevent and address technical issues;</Body2>
            </ListItem>
            <ListItem>
              <Body2>to fulfill any other purpose for which you provide it;</Body2>
            </ListItem>
            <ListItem>
              <Body2>
                to carry out our obligations and enforce our rights arising from any contracts entered into between you
                and us, including for billing and collection;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>
                to provide you with notices about your account and/or subscription, including expiration and renewal
                notices, email-instructions, etc.;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>
                to provide you with news, special offers and general information about other goods, services and events
                which we offer that are similar to those that you have already purchased or enquired about unless you
                have opted not to receive such information;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>in any other way we may describe when you provide the information;</Body2>
            </ListItem>
            <ListItem>
              <Body2>for any other purpose with your consent.</Body2>
            </ListItem>
          </OrderedList>

          <TypographyHeader as="h2">6. Retention of Data</TypographyHeader>
          <Body2>
            We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy
            Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal
            obligations (for example, if we are required to retain your data to comply with applicable laws), resolve
            disputes, and enforce our legal agreements and policies.
          </Body2>

          <Body2>
            We will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a
            shorter period, except when this data is used to strengthen the security or to improve the functionality of
            our Service, or we are legally obligated to retain this data for longer time periods.
          </Body2>

          <TypographyHeader as="h2">7. Transfer of Data</TypographyHeader>
          <Body2>
            Your information, including Personal Data, may be transferred to – and maintained on – computers located
            outside of your state, province, country or other governmental jurisdiction where the data protection laws
            may differ from those of your jurisdiction.
          </Body2>

          <Body2>
            If you are located outside United States and choose to provide information to us, please note that we
            transfer the data, including Personal Data, to United States and process it there.
          </Body2>

          <Body2>
            Your consent to this Privacy Policy followed by your submission of such information represents your
            agreement to that transfer.
          </Body2>

          <Body2>
            Help Button Inc (d.b.a Fair Square Medicare) will take all the steps reasonably necessary to ensure that
            your data is treated securely and in accordance with this Privacy Policy and no transfer of your Personal
            Data will take place to an organisation or a country unless there are adequate controls in place including
            the security of your data and other personal information.
          </Body2>

          <TypographyHeader as="h2">8. Disclosure of Data</TypographyHeader>
          <Body2>We may disclose personal information that we collect, or you provide:</Body2>

          <Body2 fontWeight="bold">Disclosure for Law Enforcement.</Body2>
          <Body2>
            Under certain circumstances, we may be required to disclose your Personal Data if required to do so by law
            or in response to valid requests by public authorities.
          </Body2>

          <Body2 fontWeight="bold">Business Transaction.</Body2>
          <Body2>
            If we or our subsidiaries are involved in a merger, acquisition or asset sale, your Personal Data may be
            transferred.
          </Body2>

          <Body2 fontWeight="bold">Other cases. We may disclose your information also:</Body2>
          <OrderedList pl={4} spacing={2} mb={6}>
            <ListItem>
              <Body2>to our subsidiaries and affiliates;</Body2>
            </ListItem>
            <ListItem>
              <Body2>to fulfill the purpose for which you provide it;</Body2>
            </ListItem>
          </OrderedList>

          <TypographyHeader as="h2">9. Security of Data</TypographyHeader>
          <Body2>
            The security of your data is important to us but remember that no method of transmission over the Internet
            or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to
            protect your Personal Data, we cannot guarantee its absolute security.
          </Body2>

          <TypographyHeader as="h2">
            10. Your Data Protection Rights Under General Data Protection Regulation (GDPR)
          </TypographyHeader>
          <Body2>
            If you are a resident of the European Union (EU) and European Economic Area (EEA), you have certain data
            protection rights, covered by GDPR. – See more at https://eur-lex.europa.eu/eli/reg/2016/679/oj
          </Body2>

          <Body2>
            We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal
            Data.
          </Body2>

          <Body2>
            If you wish to be informed what Personal Data we hold about you and if you want it to be removed from our
            systems, please email us at info@fairsquaremedicare.com.
          </Body2>

          <Body2>In certain circumstances, you have the following data protection rights:</Body2>

          <OrderedList pl={4} spacing={2} mb={6}>
            <ListItem>
              <Body2>the right to access, update or to delete the information we have on you;</Body2>
            </ListItem>
            <ListItem>
              <Body2>
                the right of rectification. You have the right to have your information rectified if that information is
                inaccurate or incomplete;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>the right to object. You have the right to object to our processing of your Personal Data;</Body2>
            </ListItem>
            <ListItem>
              <Body2>
                the right of restriction. You have the right to request that we restrict the processing of your personal
                information;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>
                the right to data portability. You have the right to be provided with a copy of your Personal Data in a
                structured, machine-readable and commonly used format;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>
                the right to withdraw consent. You also have the right to withdraw your consent at any time where we
                rely on your consent to process your personal information;
              </Body2>
            </ListItem>
          </OrderedList>

          <Body2>
            Please note that we may ask you to verify your identity before responding to such requests. Please note, we
            may not able to provide Service without some necessary data.
          </Body2>

          <Body2>
            You have the right to complain to a Data Protection Authority about our collection and use of your Personal
            Data. For more information, please contact your local data protection authority in the European Economic
            Area (EEA).
          </Body2>

          <TypographyHeader as="h2">
            11. Your Data Protection Rights under the California Privacy Protection Act (CalOPPA)
          </TypographyHeader>
          <Body2>
            CalOPPA is the first state law in the nation to require commercial websites and online services to post a
            privacy policy. The law's reach stretches well beyond California to require a person or company in the
            United States (and conceivable the world) that operates websites collecting personally identifiable
            information from California consumers to post a conspicuous privacy policy on its website stating exactly
            the information being collected and those individuals with whom it is being shared, and to comply with this
            policy. – See more at:
            <Link href="https://consumercal.org/about-cfc/cfc-education-foundation/california-online-privacy-protection-act-caloppa-3/">
              https://consumercal.org/about-cfc/cfc-education-foundation/california-online-privacy-protection-act-caloppa-3/
            </Link>
          </Body2>

          <Body2>According to CalOPPA we agree to the following:</Body2>

          <OrderedList pl={4} spacing={2} mb={6}>
            <ListItem>
              <Body2>users can visit our site anonymously;</Body2>
            </ListItem>
            <ListItem>
              <Body2>
                our Privacy Policy link includes the word "Privacy", and can easily be found on the page specified above
                on the home page of our website;
              </Body2>
            </ListItem>
            <ListItem>
              <Body2>users will be notified of any privacy policy changes on our Privacy Policy Page;</Body2>
            </ListItem>
            <ListItem>
              <Body2>
                users are able to change their personal information by emailing us at info@fairsquaremedicare.com.&nbsp;
              </Body2>
            </ListItem>
          </OrderedList>

          <Body2>Our Policy on "Do Not Track" Signals:</Body2>

          <Body2>
            We honor Do Not Track signals and do not track, plant cookies, or use advertising when a Do Not Track
            browser mechanism is in place. Do Not Track is a preference you can set in your web browser to inform
            websites that you do not want to be tracked.&nbsp;
          </Body2>

          <Body2>
            You can enable or disable Do Not Track by visiting the Preferences or Settings page of your web browser.
          </Body2>

          <TypographyHeader as="h2">12. Service Providers</TypographyHeader>
          <Body2>
            We may employ third party companies and individuals to facilitate our Service ("<b>Service Providers</b>"),
            provide Service on our behalf, perform Service-related services or assist us in analysing how our Service is
            used.
          </Body2>

          <Body2>
            These third parties have access to your Personal Data only to perform these tasks on our behalf and are
            obligated not to disclose or use it for any other purpose.
          </Body2>

          <TypographyHeader as="h2">13. Analytics</TypographyHeader>
          <Body2>We may use third-party Service Providers to monitor and analyze the use of our Service.</Body2>

          <Body2>
            <b>Google Analytics</b>
          </Body2>
          <Body2>
            Google Analytics is a web analytics service offered by Google that tracks and reports website traffic.
            Google uses the data collected to track and monitor the use of our Service. This data is shared with other
            Google services. Google may use the collected data to contextualise and personalise the ads of its own
            advertising network.
          </Body2>

          <Body2>
            For more information on the privacy practices of Google, please visit the Google Privacy Terms web page:
            https://policies.google.com/privacy?hl=en
          </Body2>

          <Body2>
            We also encourage you to review the Google's policy for safeguarding your data:
            https://support.google.com/analytics/answer/6004245.
          </Body2>

          <TypographyHeader as="h2">14. CI/CD tools</TypographyHeader>
          <Body2>
            We may use third-party Service Providers to automate the development process of our Service.&nbsp;
          </Body2>

          <Body2>
            <b>GitHub</b>
          </Body2>
          <Body2>GitHub is provided by GitHub, Inc.</Body2>

          <Body2>GitHub is a development platform to host and review code, manage projects, and build software.</Body2>

          <Body2>
            For more information on what data GitHub collects for what purpose and how the protection of the data is
            ensured, please visit GitHub Privacy Policy page:
            <Link href="https://help.github.com/en/articles/github-privacy-statement">
              {' '}
              https://help.github.com/en/articles/github-privacy-statement.
            </Link>
          </Body2>

          <TypographyHeader as="h2">15. Behavioral Remarketing</TypographyHeader>
          <Body2>
            Help Button Inc (d.b.a Fair Square Medicare) uses remarketing services to advertise on third party websites
            to you after you visited our Service. We and our third-party vendors use cookies to inform, optimise and
            serve ads based on your past visits to our Service.
          </Body2>

          <Body2>
            <b>Google Ads (AdWords)</b>
          </Body2>
          <Body2>Google Ads (AdWords) remarketing service is provided by Google Inc.</Body2>

          <Body2>
            You can opt-out of Google Analytics for Display Advertising and customize the Google Display Network ads by
            visiting the Google Ads Settings page: http://www.google.com/settings/ads
          </Body2>

          <Body2>
            Google also recommends installing the Google Analytics Opt-out Browser Add-on –
            https://tools.google.com/dlpage/gaoptout – for your web browser. Google Analytics Opt-out Browser Add-on
            provides visitors with the ability to prevent their data from being collected and used by Google Analytics.
          </Body2>

          <Body2>
            For more information on the privacy practices of Google, please visit the Google Privacy Terms web page:
            https://policies.google.com/privacy?hl=en
          </Body2>

          <Body2>
            <b>Bing Ads Remarketing</b>
          </Body2>
          <Body2>Bing Ads remarketing service is provided by Microsoft Inc.</Body2>

          <Body2>
            You can opt-out of Bing Ads interest-based ads by following their instructions:
            https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads
          </Body2>

          <Body2>
            You can learn more about the privacy practices and policies of Microsoft by visiting their Privacy Policy
            page: https://privacy.microsoft.com/en-us/PrivacyStatement
          </Body2>

          <Body2>
            <b>Twitter</b>
          </Body2>
          <Body2>Twitter remarketing service is provided by Twitter Inc.</Body2>

          <Body2>
            You can opt-out from Twitter's interest-based ads by following their instructions:
            https://support.twitter.com/articles/20170405
          </Body2>

          <Body2>
            You can learn more about the privacy practices and policies of Twitter by visiting their Privacy Policy
            page: https://twitter.com/privacy
          </Body2>

          <Body2>
            <b>Facebook</b>
          </Body2>
          <Body2>Facebook remarketing service is provided by Facebook Inc.</Body2>

          <Body2>
            You can learn more about interest-based advertising from Facebook by visiting this page:
            https://www.facebook.com/help/164968693837950
          </Body2>

          <Body2>
            To opt-out from Facebook's interest-based ads, follow these instructions from Facebook:
            https://www.facebook.com/help/568137493302217
          </Body2>

          <Body2>
            Facebook adheres to the Self-Regulatory Principles for Online Behavioural Advertising established by the
            Digital Advertising Alliance. You can also opt-out from Facebook and other participating companies through
            the Digital Advertising Alliance in the USA http://www.aboutads.info/choices/, the Digital Advertising
            Alliance of Canada in Canada http://youradchoices.ca/ or the European Interactive Digital Advertising
            Alliance in Europe http://www.youronlinechoices.eu/, or opt-out using your mobile device settings.
          </Body2>

          <Body2>
            For more information on the privacy practices of Facebook, please visit Facebook's Data Policy:
            https://www.facebook.com/privacy/explanation
          </Body2>

          <Body2>
            <b>Pinterest</b>
          </Body2>
          <Body2>Pinterest remarketing service is provided by Pinterest Inc.</Body2>

          <Body2>
            You can opt-out from Pinterest's interest-based ads by enabling the "Do Not Track" functionality of your web
            browser or by following Pinterest instructions:
            http://help.pinterest.com/en/articles/personalization-and-data
          </Body2>

          <Body2>
            You can learn more about the privacy practices and policies of Pinterest by visiting their Privacy Policy
            page: https://about.pinterest.com/en/privacy-policy
          </Body2>

          <TypographyHeader as="h2">16. Links to Other Sites</TypographyHeader>
          <Body2>
            Our Service may contain links to other sites that are not operated by us. If you click a third party link,
            you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of
            every site you visit.
          </Body2>

          <Body2>
            We have no control over and assume no responsibility for the content, privacy policies or practices of any
            third party sites or services.
          </Body2>

          <TypographyHeader as="h2">17. Children's Privacy</TypographyHeader>
          <Body2>
            Our Services are not intended for use by children under the age of 13 ("<b>Children</b>").&nbsp;
          </Body2>

          <Body2>
            We do not knowingly collect personally identifiable information from Children under 13. If you become aware
            that a Child has provided us with Personal Data, please contact us. If we become aware that we have
            collected Personal Data from Children without verification of parental consent, we take steps to remove that
            information from our servers.
          </Body2>

          <TypographyHeader as="h2">18. Changes to This Privacy Policy</TypographyHeader>
          <Body2>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new
            Privacy Policy on this page.
          </Body2>

          <Body2>
            We will let you know via email and/or a prominent notice on our Service, prior to the change becoming
            effective and update "effective date" at the top of this Privacy Policy.
          </Body2>

          <Body2>
            You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy
            are effective when they are posted on this page.
          </Body2>

          <TypographyHeader as="h2">19. Contact Us</TypographyHeader>
          <Body2>If you have any questions about this Privacy Policy, please contact us:</Body2>
          <Body2>By email: info@fairsquaremedicare.com</Body2>
        </VStack>
      </Box>
    </DefaultLayout>
  );
};

export default PrivacyPolicy;
